import { usePageBreadcrumbs } from "@/components/layout/breadcrumb-context";
import { MobileAppBadges } from "@/components/MobileAppBadges";
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { PushNotificationSettings } from "@/components/settings/PushNotificationSettings";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuthProfile } from "@/hooks/use-auth-profile";
import { useToast } from "@/hooks/use-toast";
import { fetchTierLimitsFromDB, DEFAULT_TIER_LIMITS } from "@/lib/account-tier-service";
import { trackSubscriptionCancelled } from "@/lib/analytics-service";
import { cancelSubscription, getBillingHistory, type BillingHistoryItem } from "@/lib/payment-service";
import { updateUserProfileDocument } from "@/lib/profile-service";
import type { AccountTier } from "@/lib/types";
import type { TierLimits } from "@ezrecipebox/contracts";
import { Timestamp } from "firebase/firestore";
import {
    AlertTriangle,
    Calendar,
    Check,
    CreditCard,
    Crown,
    Edit,
    ExternalLink,
    Loader2,
    Mail,
    Receipt,
    Sparkles,
    User,
} from "lucide-react";

const MIN_NAME_LENGTH = 2;

export default function ProfilePage() {
  const breadcrumbs = useMemo(() => [{ label: "Recipe Boxes", href: "/home" }, { label: "Profile" }], []);
  usePageBreadcrumbs(breadcrumbs);

  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, profile, loading } = useAuthProfile();

  const [displayName, setDisplayName] = useState("");
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarObjectUrl, setAvatarObjectUrl] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [billingHistory, setBillingHistory] = useState<BillingHistoryItem[]>([]);
  const [isLoadingBillingHistory, setIsLoadingBillingHistory] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [cancelImmediately, setCancelImmediately] = useState(false);
  const [tierLimits, setTierLimits] = useState<Record<AccountTier, TierLimits>>(DEFAULT_TIER_LIMITS);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [loading, user, navigate]);

  // Load tier limits from database
  useEffect(() => {
    const loadTierLimits = async () => {
      try {
        const limits = await fetchTierLimitsFromDB();
        setTierLimits(limits);
      } catch (error) {
        console.error("Failed to load tier limits", error);
        // Will use default tier limits from state
      }
    };

    loadTierLimits();
  }, []);

  useEffect(() => {
    if (!avatarFile && user) {
      const nameFromProfile = profile?.displayName ?? user.displayName ?? "";
      setDisplayName(nameFromProfile);
      setAvatarPreview(profile?.avatarURL ?? user.photoURL ?? null);
    }
  }, [profile, user, avatarFile]);

  useEffect(
    () => () => {
      if (avatarObjectUrl) {
        URL.revokeObjectURL(avatarObjectUrl);
      }
    },
    [avatarObjectUrl],
  );

  const handleAvatarClick = () => {
    setAvatarDialogOpen(true);
  };

  const handleAvatarChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setAvatarFile(file);

    if (avatarObjectUrl) {
      URL.revokeObjectURL(avatarObjectUrl);
      setAvatarObjectUrl(null);
    }

    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setAvatarPreview(objectUrl);
      setAvatarObjectUrl(objectUrl);
    } else {
      setAvatarPreview(profile?.avatarURL ?? user?.photoURL ?? null);
    }
  };

  const handleAvatarSave = async () => {
    if (!user || !avatarFile) {
      return;
    }

    setIsSaving(true);
    try {
      await updateUserProfileDocument(user, {
        displayName: displayName.trim() || undefined,
        avatarFile,
      });

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setAvatarFile(null);
      setAvatarObjectUrl(null);
      setAvatarDialogOpen(false);

      toast({
        title: "Avatar updated",
        description: "Your profile picture has been updated successfully.",
      });
    } catch (error) {
      console.error("Failed to update avatar", error);
      toast({
        variant: "destructive",
        title: "Update failed",
        description: "We couldn&apos;t save your avatar. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Auto-save display name with debounce
  useEffect(() => {
    if (!user) return;

    const trimmedName = displayName.trim();
    if (trimmedName.length < MIN_NAME_LENGTH) {
      setFormError(`Display name must be at least ${MIN_NAME_LENGTH} characters.`);
      return;
    }

    // Only save if display name is different from profile
    if (profile?.displayName === trimmedName) {
      setFormError(null);
      return;
    }

    setFormError(null);
    const timeoutId = setTimeout(async () => {
      setIsSaving(true);
      try {
        await updateUserProfileDocument(user, {
          displayName: trimmedName,
        });
      } catch (error) {
        console.error("Failed to update profile", error);
        setFormError("Failed to save changes. Please try again.");
      } finally {
        setIsSaving(false);
      }
    }, 800); // Debounce for 800ms

    return () => clearTimeout(timeoutId);
  }, [displayName, user, profile?.displayName]);


  const handleCancelSubscription = async () => {
    if (!user) {
      return;
    }

    setIsCanceling(true);
    try {
      const currentTier = profile?.accountTier ?? "free";
      await cancelSubscription({ cancelImmediately });
      trackSubscriptionCancelled(currentTier);
      
      const endDateStr = profile?.subscriptionCurrentPeriodEnd instanceof Timestamp
        ? profile.subscriptionCurrentPeriodEnd.toDate().toLocaleDateString("en-US", { month: "long", day: "numeric" })
        : profile?.subscriptionCurrentPeriodEnd instanceof Date
          ? profile.subscriptionCurrentPeriodEnd.toLocaleDateString("en-US", { month: "long", day: "numeric" })
          : new Date(profile?.subscriptionCurrentPeriodEnd as unknown as number).toLocaleDateString("en-US", { month: "long", day: "numeric" });
      
      toast({
        title: cancelImmediately ? "Subscription canceled" : "Subscription will cancel at period end",
        description: cancelImmediately
          ? "Your subscription has been canceled immediately. You've been downgraded to the free tier."
          : `Your subscription will end on ${endDateStr}. You'll continue to have full access to all Pro features until then.`,
      });
      setCancelDialogOpen(false);
      setCancelImmediately(false);
    } catch (error) {
      console.error("Failed to cancel subscription", error);
      toast({
        variant: "destructive",
        title: "Cancellation failed",
        description: "We couldn't cancel your subscription. Please try again or contact support.",
      });
    } finally {
      setIsCanceling(false);
    }
  };

  const finalAvatarSrc = avatarPreview || undefined;
  const avatarFallback = displayName ? displayName.slice(0, 1).toUpperCase() : "U";
  const email = profile?.email || user?.email || "";
  const currentTier = profile?.accountTier ?? "free";

  // Helper function to check if subscription has active days remaining
  const getSubscriptionStatus = () => {
    if (!profile?.subscriptionCurrentPeriodEnd) {
      return { isValid: false, daysRemaining: 0, isActive: false, isCanceled: false };
    }

    const endDate = profile.subscriptionCurrentPeriodEnd instanceof Timestamp
      ? profile.subscriptionCurrentPeriodEnd.toDate()
      : profile.subscriptionCurrentPeriodEnd instanceof Date
        ? profile.subscriptionCurrentPeriodEnd
        : new Date(profile.subscriptionCurrentPeriodEnd as unknown as number);

    const now = new Date();
    const isExpired = endDate < now;
    const isActive = profile?.subscriptionStatus === "active";
    const isCanceled = profile?.subscriptionStatus === "canceled" || profile?.subscriptionStatus === "refunded";
    const daysRemaining = isExpired ? 0 : Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    return {
      isValid: !isExpired && (isActive || isCanceled),
      daysRemaining,
      isActive,
      isCanceled,
    };
  };

  const subscriptionStatus = getSubscriptionStatus();

  // Check for active subscription via platform (Apple/Google Pay)
  // Include both active subscriptions AND canceled subscriptions that still have days remaining
  const hasActiveSubscription =
    (profile?.platformSubscriptionId &&
     (profile?.subscriptionStatus === "active" || profile?.subscriptionStatus === "canceled" || profile?.subscriptionStatus === "refunded") &&
     subscriptionStatus.isValid);


  useEffect(() => {
    // Load billing history if user has active subscription
    if (!hasActiveSubscription) {
      return;
    }

    let isMounted = true;
    setIsLoadingBillingHistory(true);
    getBillingHistory(5)
      .then((result) => {
        if (isMounted) {
          setBillingHistory(result.items);
        }
      })
      .catch((error) => {
        console.error("Failed to load billing history", error);
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingBillingHistory(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [hasActiveSubscription]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container max-w-6xl mx-auto px-4 py-4 space-y-3">
      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-3xl font-semibold tracking-tight">Profile Settings</h1>
        <p className="text-muted-foreground text-sm">Manage your account information and subscription.</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-3 auto-rows-fr">
        {/* Left Column - Personal Information */}
        <div className="lg:col-span-1">
          {/* Personal Information & Avatar */}
          <Card className="flex flex-col items-start w-full h-full">
            <CardHeader className="w-full">
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Personal Information
              </CardTitle>
              <CardDescription>Update your profile picture, display name, and contact information.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 w-full flex-1 flex flex-col">
              <div className="space-y-4">
                <div className="space-y-4">
                {/* Avatar Section */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2.5 pb-2 border-b">
                  <div className="flex flex-col items-center space-y-1">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={handleAvatarClick}
                            className="relative group cursor-pointer"
                          >
                            <Avatar className="h-16 w-16 border-2 border-background shadow-lg ring-2 ring-primary/20 transition-all group-hover:ring-primary/40 group-hover:scale-105">
                              {finalAvatarSrc ? (
                                <AvatarImage src={finalAvatarSrc} alt={displayName || "Profile avatar"} />
                              ) : null}
                              <AvatarFallback className="text-2xl font-medium bg-gradient-to-br from-primary/20 to-primary/10">
                                {avatarFallback}
                              </AvatarFallback>
                            </Avatar>
                            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                              <Edit className="h-5 w-5 text-white" />
                            </div>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Click to change avatar</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <p className="text-xs text-muted-foreground text-center max-w-[120px]">
                      Click to upload a new profile picture
                    </p>
                  </div>
                  <div className="flex-1 space-y-1">
                    <h3 className="font-semibold text-lg">{displayName || "User"}</h3>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {email}
                    </p>
                  </div>
                </div>

                {/* Form Fields */}
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="displayName" className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Display Name
                    </Label>
                    <Input
                      id="displayName"
                      value={displayName}
                      onChange={(event) => setDisplayName(event.target.value)}
                      disabled={isSaving}
                      placeholder="Your name"
                      className="max-w-md"
                    />
                  </div>


                </div>

                {formError ? (
                  <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive flex items-center gap-2">
                    <span>{formError}</span>
                    {isSaving && <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />}
                  </div>
                ) : isSaving ? (
                  <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-900 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Saving...</span>
                  </div>
                ) : null}
                <p className="text-xs text-muted-foreground mt-2">
                  Email cannot be changed. Contact support if you need to update your email address.
                </p>
                </div>
              </div>

              {/* Push Notifications Toggle */}
              <PushNotificationSettings compact />
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Subscription & Billing */}
        <div className="lg:col-span-2 space-y-4">
          {/* Subscription & Billing Combined Card */}
          <Card className="flex flex-col items-start w-full h-full">
            <CardHeader className="w-full">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Crown className="h-5 w-5" />
                    Subscription & Billing
                  </CardTitle>
                  <CardDescription>Subscriptions are managed on the mobile device.</CardDescription>
                </div>
                <div className="flex items-start gap-2 flex-shrink-0">
                  <MobileAppBadges className="scale-90" />
                  {hasActiveSubscription && (
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/billing">
                        <CreditCard className="h-4 w-4 mr-2" />
                        Manage Billing
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 flex-1 flex flex-col pb-1.5 w-full">
              {/* Billing History Summary */}
              {hasActiveSubscription && (
                <>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold">Recent Billing History</h3>
                      <Button variant="ghost" size="sm" asChild>
                        <Link to="/billing">
                          View All
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </Link>
                      </Button>
                    </div>
                    {isLoadingBillingHistory ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    ) : billingHistory.length > 0 ? (
                      <div className="space-y-2">
                        {billingHistory.slice(0, 3).map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center justify-between rounded-md border p-3 text-sm"
                          >
                            <div className="flex items-center gap-3">
                              <Receipt className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p className="font-medium">{item.description}</p>
                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {new Date(item.date).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="font-medium">
                                ${(item.amount / 100).toFixed(2)} {item.currency.toUpperCase()}
                              </span>
                              {item.receiptUrl && (
                                <Button variant="ghost" size="sm" asChild>
                                  <a href={item.receiptUrl} target="_blank" rel="noopener noreferrer">
                                    <ExternalLink className="h-3 w-3" />
                                  </a>
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No billing history available
                      </p>
                    )}
                  </div>
                  <Separator />
                </>
              )}

              {/* Subscription Cancellation */}
              {hasActiveSubscription && subscriptionStatus.isActive && (
                <>
                  <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 space-y-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-destructive mt-0.5" />
                      <div className="flex-1 space-y-2">
                        <p className="text-sm font-medium">Cancel Subscription</p>
                        <p className="text-xs text-muted-foreground">
                          Cancel your subscription at any time. You&apos;ll continue to have access until the end of your
                          billing period.
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setCancelDialogOpen(true)}
                      className="w-full sm:w-auto"
                    >
                      Cancel Subscription
                    </Button>
                  </div>
                  <Separator className="my-1" />
                </>
              )}

              {/* Subscription Cancellation Notice */}
              {hasActiveSubscription && subscriptionStatus.isCanceled && subscriptionStatus.isValid && (
                <>
                  <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 p-4 space-y-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5" />
                      <div className="flex-1 space-y-2">
                        <p className="text-sm font-medium text-amber-900 dark:text-amber-100">Subscription Canceling</p>
                        <p className="text-xs text-amber-800 dark:text-amber-200">
                          Your subscription will end on {profile?.subscriptionCurrentPeriodEnd instanceof Timestamp
                            ? profile.subscriptionCurrentPeriodEnd.toDate().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
                            : profile?.subscriptionCurrentPeriodEnd instanceof Date
                              ? profile.subscriptionCurrentPeriodEnd.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
                              : new Date(profile?.subscriptionCurrentPeriodEnd as unknown as number).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}. You&apos;ll
                          continue to have full access to all features until then.
                        </p>
                      </div>
                    </div>
                  </div>
                  <Separator className="my-1" />
                </>
              )}

              {/* Available Plans */}
              <div className="space-y-1.5 -mt-1.5">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Available Plans</h3>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-xs text-muted-foreground cursor-help">
                          Change your plan anytime
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>You can upgrade or downgrade your plan at any time</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="grid gap-3 md:grid-cols-2 md:items-stretch mt-1">
                  {(["free", "full_pro"] as AccountTier[]).map((tier) => {
                    const info = tierLimits[tier];
                    const isCurrentTier = tier === currentTier;
                    const isUpgrade = tier === "full_pro" && currentTier !== "full_pro";

                    const TierIcon = tier === "full_pro" ? Crown : Sparkles;

                    return (
                      <Card
                        key={tier}
                        className={`relative transition-all flex flex-col ${
                          isCurrentTier
                            ? "border-primary shadow-md ring-2 ring-primary/20"
                            : "hover:border-primary/50 hover:shadow-sm"
                        }`}
                      >
                        {isCurrentTier && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                            <Badge className="bg-primary">Current</Badge>
                          </div>
                        )}
                        <CardHeader className="pb-3">
                          <div className="flex items-center gap-2 mb-2">
                            <TierIcon
                              className={`h-5 w-5 ${
                                isCurrentTier ? "text-primary" : "text-muted-foreground"
                              }`}
                            />
                            <CardTitle>
                              {tier === "free" ? "Free" : "Full Pro"}
                            </CardTitle>
                          </div>
                  <CardDescription>
                    {tier === "free"
                      ? "Ad-supported account with limited sharing"
                      : "Unlimited recipes, no ads"}
                  </CardDescription>
                        </CardHeader>
                        <CardContent className="flex flex-col flex-1 min-h-0 pb-2">
                          <div className="space-y-1 flex-1 min-h-0">
                            <div className="space-y-1">
                              {/* Recipe Boxes limit */}
                              <div className="flex items-center gap-2 text-sm">
                                {tier === "free" ? (
                                  <>
                                    <Sparkles className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                    <span className="text-muted-foreground">{info.maxRecipeBoxes} Recipe Boxes</span>
                                  </>
                                ) : (
                                  <>
                                    <Crown className="h-4 w-4 text-primary flex-shrink-0" />
                                    <span className="text-muted-foreground">Unlimited Recipe Boxes</span>
                                  </>
                                )}
                              </div>

                              {/* Recipes per box limit */}
                              <div className="flex items-center gap-2 text-sm">
                                {tier === "free" ? (
                                  <>
                                    <Sparkles className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                    <span className="text-muted-foreground">{info.maxRecipesPerBox} Recipes per Box</span>
                                  </>
                                ) : (
                                  <>
                                    <Crown className="h-4 w-4 text-primary flex-shrink-0" />
                                    <span className="text-muted-foreground">Unlimited Recipes</span>
                                  </>
                                )}
                              </div>

                              {/* Members per box limit */}
                              <div className="flex items-center gap-2 text-sm">
                                {tier === "free" ? (
                                  <>
                                    <Sparkles className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                    <span className="text-muted-foreground">{info.maxMembersPerBox} Members to Share With</span>
                                  </>
                                ) : (
                                  <>
                                    <Crown className="h-4 w-4 text-primary flex-shrink-0" />
                                    <span className="text-muted-foreground">Unlimited Members</span>
                                  </>
                                )}
                              </div>

                              {/* Ads */}
                              <div className="flex items-center gap-2 text-sm">
                                {tier === "free" ? (
                                  <>
                                    <Sparkles className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                    <span className="text-muted-foreground">Ad-supported</span>
                                  </>
                                ) : (
                                  <>
                                    <Crown className="h-4 w-4 text-primary flex-shrink-0" />
                                    <span className="text-muted-foreground">No ads</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="mt-auto flex-shrink-0 pt-1.5">
                          {isCurrentTier ? (
                            <Button className="w-full" variant="outline" disabled>
                              <Check className="h-4 w-4 mr-2" />
                              Current Plan
                            </Button>
                          ) : (
                            <div className="w-full rounded-md bg-primary/10 text-center text-sm font-medium text-primary py-2.5">
                              {isUpgrade ? "Upgrade" : "Downgrade"} via the mobile app
                            </div>
                          )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Push Notifications moved to Personal Information Card */}

          {/* Push notifications (web) removed: service workers/PWA are disabled; mobile experience will be Flutter. */}
        </div>
      </div>

      {/* Legal Links */}
      <div className="pt-2 border-t text-center">
        <p className="text-xs text-muted-foreground">
          <Link to="/privacy" className="hover:underline underline-offset-4">Privacy Policy</Link>
          {" · "}
          <Link to="/terms" className="hover:underline underline-offset-4">Terms of Service</Link>
        </p>
      </div>

      {/* Avatar Edit Dialog */}
      <Dialog open={avatarDialogOpen} onOpenChange={setAvatarDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Avatar</DialogTitle>
            <DialogDescription>Upload a new profile picture. JPG or PNG, max 5MB.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-center">
              <Avatar className="h-32 w-32">
                {avatarPreview ? (
                  <AvatarImage src={avatarPreview} alt="Preview" />
                ) : null}
                <AvatarFallback className="text-3xl">{avatarFallback}</AvatarFallback>
              </Avatar>
            </div>
            <div className="space-y-2">
              <Label htmlFor="avatar-upload">Choose Image</Label>
              <Input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                disabled={isSaving}
                ref={fileInputRef}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAvatarDialogOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleAvatarSave} disabled={isSaving || !avatarFile}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Subscription</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-3">
                <p>How would you like to cancel your subscription?</p>
                <div className="space-y-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="cancelType"
                      checked={!cancelImmediately}
                      onChange={() => setCancelImmediately(false)}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium text-sm">Cancel at period end (Recommended)</p>
                      <p className="text-xs text-muted-foreground">
                        Keep your subscription active until the end of your current billing period. You&apos;ll continue
                        to have access to all features until then.
                      </p>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="cancelType"
                      checked={cancelImmediately}
                      onChange={() => setCancelImmediately(true)}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium text-sm">Cancel immediately</p>
                      <p className="text-xs text-muted-foreground">
                        Cancel right away and be downgraded to the free tier immediately. You&apos;ll lose access to
                        premium features right away.
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCanceling}>Keep Subscription</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelSubscription}
              disabled={isCanceling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isCanceling ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Canceling...
                </>
              ) : (
                "Confirm Cancellation"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

